import { useParams } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';

interface Account {
  id: string;
  name: string;
  code?: string;
  accountNumber?: string;
  externalId?: string;
  status: string;
  industry?: string;
  website?: string;
  creditHold?: boolean;
  notes?: string;
  createdAt?: string;
}

export function AccountDetailPage() {
  const { id } = useParams();
  const { sessionId } = useAuth();

  const { data: account, isLoading, error } = useQuery({
    queryKey: ['account', id, sessionId],
    queryFn: async () => {
      const result = await api.get<{ account: Account }>(`/accounts/${id}`, sessionId);
      return result.account;
    },
    enabled: !!sessionId && !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900">Account Not Found</h1>
        <p className="text-gray-500 mt-2">The account you're looking for doesn't exist or you don't have permission to view it.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{account.name}</h1>
            {account.creditHold && (
              <Badge variant="destructive">Credit Hold</Badge>
            )}
          </div>
          {account.code && (
            <p className="text-sm text-gray-500 mt-1">Code: {account.code}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <p className="mt-1">
                <Badge variant={account.status === 'active' ? 'default' : 'secondary'}>
                  {account.status}
                </Badge>
              </p>
            </div>
            {account.accountNumber && (
              <div>
                <label className="text-sm font-medium text-gray-500">Reference Number</label>
                <p className="mt-1 text-sm">{account.accountNumber}</p>
              </div>
            )}
            {account.externalId && (
              <div>
                <label className="text-sm font-medium text-gray-500">External ID</label>
                <p className="mt-1 text-sm font-mono">{account.externalId}</p>
              </div>
            )}
            {account.industry && (
              <div>
                <label className="text-sm font-medium text-gray-500">Industry</label>
                <p className="mt-1 text-sm capitalize">{account.industry}</p>
              </div>
            )}
            {account.website && (
              <div>
                <label className="text-sm font-medium text-gray-500">Website</label>
                <p className="mt-1 text-sm">
                  <a href={account.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {account.website}
                  </a>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {account.notes && (
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{account.notes}</p>
            </CardContent>
          </Card>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Locations</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">Location management coming soon...</p>
        </CardContent>
      </Card>
    </div>
  );
}
